import type {
  BoardDocument,
  BoardShape,
} from "../../../../api/src/workspace/board-schema";
export type Shape = BoardShape;
export type Board = BoardDocument;
export type Point = [number, number];
export type Tool =
  | "select"
  | "hand"
  | "pen"
  | "highlight"
  | "erase"
  | "rect"
  | "ellipse"
  | "line"
  | "arrow"
  | "triangle"
  | "diamond";
export const COLORS = [
  "#172554",
  "#64748b",
  "#dc2626",
  "#ea580c",
  "#eab308",
  "#16a34a",
  "#0284c7",
  "#7c3aed",
  "#db2777",
  "#ffffff",
];
export function bounds(s: Shape) {
  const xs = s.points.map((p) => p[0]),
    ys = s.points.map((p) => p[1]);
  if (s.kind === "text" && s.points.length === 1)
    return {
      x: xs[0],
      y: Math.max(0, ys[0] - 24),
      w: Math.max(
        1,
        Math.min(1000 - xs[0], Math.max(40, (s.text?.length ?? 1) * 14)),
      ),
      h: 32,
    };
  return {
    x: Math.min(...xs),
    y: Math.min(...ys),
    w: Math.max(1, Math.max(...xs) - Math.min(...xs)),
    h: Math.max(1, Math.max(...ys) - Math.min(...ys)),
  };
}
export function translated(s: Shape, dx: number, dy: number): Shape {
  const b = bounds(s);
  dx = Math.max(-b.x, Math.min(1000 - b.x - b.w, dx));
  dy = Math.max(-b.y, Math.min(600 - b.y - b.h, dy));
  return {
    ...s,
    points: s.points.map(([x, y]) => [
      Math.max(0, Math.min(1000, x + dx)),
      Math.max(0, Math.min(600, y + dy)),
    ]),
  };
}
export function resized(s: Shape, p: Point, keepRatio = false): Shape {
  const b = bounds(s);
  let w = Math.max(12, p[0] - b.x),
    h = Math.max(12, p[1] - b.y);
  if (keepRatio) {
    const scale = Math.min(
      w / b.w,
      h / b.h,
      (1000 - b.x) / b.w,
      (600 - b.y) / b.h,
    );
    w = b.w * scale;
    h = b.h * scale;
  }
  w = Math.min(w, 1000 - b.x);
  h = Math.min(h, 600 - b.y);
  const points: Point[] =
    s.points.length === 1
      ? [
          [b.x, b.y],
          [b.x + w, b.y + h],
        ]
      : s.points.map(([x, y]) => [
          b.x + ((x - b.x) / b.w) * w,
          b.y + ((y - b.y) / b.h) * h,
        ]);
  return { ...s, points };
}
export function boardLimit(shapes: Shape[]) {
  if (
    shapes.length > 200 ||
    shapes.reduce((n, s) => n + s.points.length, 0) > 5000 ||
    new TextEncoder().encode(JSON.stringify(shapes)).length > 79000
  )
    return "This board is full. Export a copy, then remove objects or clear the board.";
  if (shapes.filter((s) => s.kind === "image").length > 20)
    return "A board can contain up to 20 images. Remove an image before adding another.";
  return "";
}
